/** Catches a render error so one broken screen never blanks the whole app (0.9.12, architecture review #4).
 * Used once at the root (around everything) and once per screen (inside the app shell, keyed by the path,
 * so moving to another screen starts fresh). Shows a calm card with "Reload"; the error goes to the
 * console (and to error tracking, when the page has one installed as `window.Sentry`). */
import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode; where: "app" | "screen" };
type State = { error: Error | null };

type SentryLike = { captureException?: (e: unknown, ctx?: unknown) => void };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("screen failed to render", error, info.componentStack);
    const sentry = (window as unknown as { Sentry?: SentryLike }).Sentry;
    sentry?.captureException?.(error, { extra: { where: this.props.where } });
  }

  render() {
    if (!this.state.error) return this.props.children;
    const screen = this.props.where === "screen";
    return (
      <section className="card error-boundary" role="alert" aria-labelledby="error-boundary-title">
        <h2 id="error-boundary-title">{screen ? "This screen couldn't be shown" : "Something went wrong"}</h2>
        <p className="muted">
          {screen
            ? "Something went wrong on this screen. Your data is safe. Reload to try again, or open another screen."
            : "Something went wrong in the app. Your data is safe. Reload to try again."}
        </p>
        <div className="row">
          <button type="button" className="primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </section>
    );
  }
}
